import { Component, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { ProductService } from 'src/app/core/api/product/product.service';
import { FamilyRequestDTO } from 'src/app/shared/models/dtos/requests/family-request-dto/family-request-dto';
import { selectableRegions } from 'src/app/utils/regions';
import { UserService } from 'src/app/core/api/user/user.service';
import { SelectorData } from 'src/app/utils/selectorDataType';
import { MatDialogResponse } from 'src/app/shared/models/constants/mat-dialog-response';
import { Fields } from 'src/app/utils/fields';
import { VipLevelSettings } from 'src/app/shared/models/constants/vip-level-settings';
import { VipFormBuilderService } from 'src/app/core/services/vip-form-builder/vip-form-builder.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';

@Component({
  selector: 'app-family-modal',
  templateUrl: './family-modal.component.html',
  styleUrls: ['./family-modal.component.scss'],
})
/**
 * Modal for create new family
 */
export class FamilyModalComponent implements OnInit {
  labels = {
    saveChanges: 'save-changes',
    cancel: 'cancel',
    vipLevelName: 'vip-level-name',
    inkRequired: 'ink-required-points-to-level',
    discount: 'discount',
    createTitle: 'series-form-modal.title-create',
    editSeriesTitle: 'series-form-modal.title-edit-series',
    editVipTitle: 'series-form-modal.title-edit-vip',
    nameLabel: 'series-form-modal.name-label',
    tableCreateTitle: 'series-form-modal.table-create-title',
    tableEditTitle: 'series-form-modal.table-edit-title',
    currentSeason: 'current-season',
    nextSeason: 'next-season-capitalized',
    startsOn: 'starts-on',
    endsOn: 'ends-on',
    changesApplyAllRegions: 'changes-apply-all-regions',
    savingError: 'unexpected-error',
    success: 'success-updating-data',
    addVipLevel: 'series-form-modal.add-vip-level',
    emptyList: 'series-form-modal.empty-list',
    successfullyMsg: 'series-form-modal.successfully-msg',
  };
  errorMsg = {
    requiredMsg: 'series-form-modal.error-messages.required',
    specialCharacterMsg: 'series-form-modal.error-messages.special-character',
    minLengthMsg: 'series-form-modal.error-messages.min-length',
    minInkLevelValueMsg: 'series-form-modal.error-messages.min-ink-level-value',
    maxInkLevelValueMsg: 'series-form-modal.error-messages.max-ink-level-value',
    minDiscountValueMsg: 'series-form-modal.error-messages.min-discount-value',
    maxDiscountValueMsg: 'series-form-modal.error-messages.max-discount-value',
  };
  formFamily = new FormGroup({
    name: new FormControl('', [Validators.required, Validators.maxLength(3)]),
  });
  formVip: FormGroup = new FormGroup({});
  vipLevels: VipLevelSettings[] = [
    { label: 'VIP 1', id: 'vip_1', markedForDeletion: false },
  ];
  vipFields = [
    Fields.LABEL,
    Fields.BUSINESS_LEVEL_ID,
    Fields.NAME,
    Fields.LEVEL_POINTS,
    Fields.DISCOUNT,
  ];
  availableRegions = selectableRegions;
  currentRegion?: SelectorData;
  allRegions: number[] = [];
  loading = false;

  /**
   * constructor
   */
  constructor(
    private readonly translationService: TranslationService,
    private dialogRef: MatDialogRef<FamilyModalComponent>,
    private readonly productService: ProductService,
    private userService: UserService,
    private readonly vipFormBuilder: VipFormBuilderService,
    private readonly snackBarService: SnackBarService
  ) {}

  /**
   * On init
   */
  ngOnInit(): void {
    this.translationService.translateObject(this.labels);
    this.translationService.translateObject(this.errorMsg);
    this.currentRegion = this.availableRegions[1];

    this.userService.getRegions().subscribe((regions) => {
      this.allRegions = regions.map((region) => region.id);
    });

    this.createVipSettingsForms();
  }

  /**
   * Create Vip Settings Form
   */
  createVipSettingsForms(): void {
    this.formVip = this.vipFormBuilder.createVipForm(
      this.vipLevels,
      this.vipFields
    );
  }

  /**
   * Cancel button click action
   */
  clickCancel(): void {
    this.dialogRef.close();
  }

  /**
   * Save button click action
   */
  clickSave(): void {
    this.loading = true;
    this.saveNewFamily();
  }

  /**
   * retrieves the form control associated with the specified family and language.
   * @param {string} vipLevel
   * @param {string} field
   * @return {FormControl}
   */
  getControlByVipType(vipLevel: string, field: string): FormControl {
    return this.formVip.get([vipLevel, field].join('.')) as FormControl;
  }

  /**
   * @param {string} controlName
   * @return {FormControl}
   */
  getFamilyControl(controlName: string): FormControl {
    return this.formFamily.get(controlName) as FormControl;
  }

  /**
   * call to the service and save a new family
   */
  saveNewFamily(): void {
    if (this.formFamily.invalid) {
      return;
    }

    const form = this.formFamily.value as FamilyRequestDTO;
    const vipForm = this.formVip.value;
    const familyConfigs = Object.values(vipForm);

    const data = {
      name: form.name,
      familyConfigs: familyConfigs,
    };
    this.productService.createFamilyProduct(data).subscribe({
      complete: () => {
        this.snackBarService.success(this.labels.successfullyMsg);
        this.dialogRef.close(MatDialogResponse.SUCCESS);
        this.loading = false;
      },
      error: async (err) => {
        if (err.error.errorMessage) {
          this.snackBarService.error(err.error.errorMessage);
        } else {
          this.snackBarService.error(this.labels.savingError);
        }
        this.loading = false;
      },
    });
  }

  /**
   * Define generic errors filtered by control touched or dirty
   * @return {string[]}
   */
  getGenericErrorsFiltered(): string[] {
    const messages: string[] = [];
    Object.keys(this.formVip.controls).forEach((vipId) => {
      const group = this.formVip.get(vipId) as FormGroup;
      this.collectGroupErrors(group, messages);
    });
    return [...new Set(messages)];
  }

  /**
   * @param {FormGroup} group
   * @param {string[]} messages
   */
  private collectGroupErrors(group: FormGroup, messages: string[]): void {
    Object.keys(group.controls).forEach((field) => {
      const control = group.get(field);
      if (control?.errors && (control.touched || control.dirty)) {
        messages.push(...this.getFieldErrorMessages(field, control.errors));
      }
    });
  }

  /**
   * @param {string} field
   * @param {ValidationErrors} errors
   * @return {string[]}
   */
  private getFieldErrorMessages(
    field: string,
    errors: ValidationErrors
  ): string[] {
    const msgs: string[] = [];
    if (errors['required']) msgs.push(this.errorMsg.requiredMsg);
    if (errors['minlength']) msgs.push(this.errorMsg.minLengthMsg);
    if (errors['pattern']) msgs.push(this.errorMsg.specialCharacterMsg);
    if (errors['min']) {
      msgs.push(
        field === Fields.LEVEL_POINTS
          ? this.errorMsg.minInkLevelValueMsg
          : this.errorMsg.minDiscountValueMsg
      );
    }
    if (errors['max']) {
      msgs.push(
        field === Fields.LEVEL_POINTS
          ? this.errorMsg.maxInkLevelValueMsg
          : this.errorMsg.maxDiscountValueMsg
      );
    }
    return msgs;
  }

  /**
   * Add a new line on the vip settings forms
   */
  addNewLine() {
    const newVipLevel = { label: '', id: '', markedForDeletion: false };
    this.vipLevels.push(newVipLevel);
    this.reindexVipLevelsAndForm();
  }

  /**
   * Deletes line on the vip settings forms
   * @param {string} vipId
   */
  deleteLine(vipId: string) {
    this.vipLevels = this.vipLevels.filter((vip) => vip.id !== vipId);
    this.reindexVipLevelsAndForm();
  }

  /**
   * Adjust all label from the vip levels
   */
  private reindexVipLevelsAndForm() {
    const currentValues = this.formVip.value;
    const newForm = new FormGroup({});
    this.vipLevels.forEach((vip, index) => {
      const oldId = vip.id;
      const newId = 'vip_' + (index + 1);
      const newLabel = 'VIP ' + (index + 1);
      vip.id = newId;
      vip.label = 'VIP ' + (index + 1);
      const newFormGroup = this.vipFormBuilder.createFormGroup(
        vip,
        this.vipFields
      );
      if (currentValues[oldId]) {
        newFormGroup.patchValue(currentValues[oldId]);
      }
      newFormGroup.get('label')?.setValue(newLabel, { emitEvent: false });
      newForm.addControl(newId, newFormGroup);
    });
    this.formVip = newForm;
  }
}
